import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  FormHelperText,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NoteAdd as NoteAddIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';
import { consultasIniciais, pacientesIniciais } from '../shared/initialData';
import { format, parse, addDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

const Consultas = () => {
  const [consultas, setConsultas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAcompanhamentoDialog, setOpenAcompanhamentoDialog] = useState(false);
  const [currentConsulta, setCurrentConsulta] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [psicologoId, setPsicologoId] = useState(null);
  const [acompanhamento, setAcompanhamento] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState([
    {
      id: 1,
      tipo: 'reagendamento',
      consultaId: 2,
      pacienteId: 2,
      dataAtual: '15/03/2024',
      horarioAtual: '14:30',
      dataSolicitada: '18/03/2024',
      horarioSolicitado: '16:00',
      motivo: 'Compromisso de trabalho no horário original',
      status: 'pendente'
    },
    {
      id: 2,
      tipo: 'novaConsulta',
      pacienteId: 3,
      dataSolicitada: '21/03/2024',
      horarioSolicitado: '10:00',
      tipoConsulta: 'Online',
      observacoes: 'Prefiro pela manhã, se possível',
      status: 'pendente'
    }
  ]);

  const [formData, setFormData] = useState({
    pacienteId: '',
    data: null,
    horario: null,
    duracao: '50',
    tipo: 'Presencial',
    plataforma: '',
    status: 'Agendada',
    observacoes: '',
    isRecorrente: false,
    diaSemanaRecorrente: '',
    horarioRecorrente: '',
    duracaoRecorrente: '50',
    dataFimRecorrencia: '',
    tipoRecorrencia: 'semanal',
    intervaloRecorrencia: 1
  });

  // Verifica o psicólogo logado e carrega suas consultas
  useEffect(() => {
    // Verifica se o usuário é psicólogo
    const tipoUsuario = localStorage.getItem('userType');
    if (tipoUsuario !== 'psicologo') {
      return;
    }

    const storedPsicologoId = localStorage.getItem('userId');
    if (storedPsicologoId) {
      setPsicologoId(parseInt(storedPsicologoId));
      
      // Filtra consultas do psicólogo
      const consultasFiltradas = consultasIniciais.filter(
        consulta => consulta.psicologoId === parseInt(storedPsicologoId)
      );
      setConsultas(consultasFiltradas);
      
      // Filtra pacientes do psicólogo
      const pacientesFiltrados = pacientesIniciais.filter(
        paciente => paciente.psicologoId === parseInt(storedPsicologoId)
      );
      setPacientes(pacientesFiltrados);
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (consulta = null) => {
    if (consulta) {
      // Editar consulta existente
      const dataArray = consulta.data.split('/');
      const data = new Date(dataArray[2], dataArray[1] - 1, dataArray[0]);
      
      const [hora, minuto] = consulta.horario.split(':');
      const horario = new Date();
      horario.setHours(hora);
      horario.setMinutes(minuto);
      
      setFormData({
        pacienteId: consulta.pacienteId.toString(),
        data: data,
        horario: horario,
        duracao: consulta.duracao,
        tipo: consulta.tipo,
        plataforma: consulta.plataforma,
        status: consulta.status,
        observacoes: consulta.observacoes,
        isRecorrente: false,
        diaSemanaRecorrente: '',
        horarioRecorrente: '',
        duracaoRecorrente: '50',
        dataFimRecorrencia: '',
        tipoRecorrencia: 'semanal',
        intervaloRecorrencia: 1
      });
      setCurrentConsulta(consulta);
    } else {
      // Nova consulta
      setFormData({
        pacienteId: '',
        data: null,
        horario: null,
        duracao: '50',
        tipo: 'Presencial',
        plataforma: '',
        status: 'Agendada',
        observacoes: '',
        isRecorrente: false,
        diaSemanaRecorrente: '',
        horarioRecorrente: '',
        duracaoRecorrente: '50',
        dataFimRecorrencia: '',
        tipoRecorrencia: 'semanal',
        intervaloRecorrencia: 1
      });
      setCurrentConsulta(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
  };

  const handleOpenAcompanhamentoDialog = (consulta) => {
    setCurrentConsulta(consulta);
    setAcompanhamento('');
    setOpenAcompanhamentoDialog(true);
  };

  const handleCloseAcompanhamentoDialog = () => {
    setOpenAcompanhamentoDialog(false);
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Limpa o erro quando o usuário corrige o campo
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: null
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.pacienteId) errors.pacienteId = 'Selecione um paciente';
    
    if (!formData.isRecorrente) {
      if (!formData.data) errors.data = 'Selecione uma data';
      if (!formData.horario) errors.horario = 'Selecione um horário';
      if (!formData.duracao) errors.duracao = 'Informe a duração';
    } else {
      if (!formData.diaSemanaRecorrente) errors.diaSemanaRecorrente = 'Selecione o dia da semana';
      if (!formData.horarioRecorrente) errors.horarioRecorrente = 'Informe o horário';
    }
    
    if (formData.tipo === 'Online' && !formData.plataforma) {
      errors.plataforma = 'Informe a plataforma para consulta online';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveConsulta = () => {
    if (!validateForm()) return;
    
    if (formData.isRecorrente) {
      handleSaveConsultaRecorrente();
      return;
    }
    
    const dataFormatada = formData.data.toLocaleDateString('pt-BR');
    const horarioFormatado = `${formData.horario.getHours().toString().padStart(2, '0')}:${formData.horario.getMinutes().toString().padStart(2, '0')}`;
    
    if (currentConsulta) {
      // Atualizar consulta existente
      const consultasAtualizadas = consultas.map(c => {
        if (c.id === currentConsulta.id) {
          return {
            ...c,
            pacienteId: parseInt(formData.pacienteId),
            data: dataFormatada,
            horario: horarioFormatado,
            duracao: formData.duracao,
            tipo: formData.tipo,
            plataforma: formData.plataforma,
            status: formData.status,
            observacoes: formData.observacoes
          };
        }
        return c;
      });
      setConsultas(consultasAtualizadas);
    } else {
      // Criar nova consulta
      const novaConsulta = {
        id: Math.max(...consultas.map(c => c.id), 0) + 1,
        pacienteId: parseInt(formData.pacienteId),
        data: dataFormatada,
        horario: horarioFormatado,
        duracao: formData.duracao,
        tipo: formData.tipo,
        plataforma: formData.plataforma,
        status: formData.status,
        observacoes: formData.observacoes,
        psicologoId: psicologoId,
        acompanhamentos: []
      };
      setConsultas([...consultas, novaConsulta]);
    }
    
    handleCloseDialog();
  };

  // Função para criar consultas recorrentes
  const handleSaveConsultaRecorrente = () => {
    const pacienteId = parseInt(formData.pacienteId);
    const paciente = pacientes.find(p => p.id === pacienteId);
    if (!paciente) {
      alert('Paciente não encontrado.');
      return;
    }
    
    // Dias da semana com índice numérico
    const diasSemanaIndices = {
      'domingo': 0,
      'segunda': 1,
      'terca': 2,
      'quarta': 3,
      'quinta': 4,
      'sexta': 5,
      'sabado': 6
    };
    
    const diaSemanaIndex = diasSemanaIndices[formData.diaSemanaRecorrente];
    if (diaSemanaIndex === undefined) {
      alert('Dia da semana inválido.');
      return;
    }
    
    // Cria a data de início para o próximo dia da semana correspondente
    const dataInicio = new Date();
    const dataFimRecorrencia = formData.dataFimRecorrencia ? new Date(formData.dataFimRecorrencia) : null;
    
    // Ajusta para o próximo dia da semana correspondente
    const [horas, minutos] = formData.horarioRecorrente.split(':').map(Number);
    const hoje = dataInicio.getDay();
    const diff = (diaSemanaIndex + 7 - hoje) % 7;
    dataInicio.setDate(dataInicio.getDate() + (diff === 0 ? 7 : diff)); // Se for hoje, vai para a próxima semana
    
    // Gera os eventos recorrentes até a data limite (ou 6 meses no máximo)
    const seisMesesDepois = new Date();
    seisMesesDepois.setMonth(seisMesesDepois.getMonth() + 6);
    
    const dataLimite = dataFimRecorrencia || seisMesesDepois;
    const consultasRecorrentes = [];
    const dataAtual = new Date(dataInicio);
    
    let idBase = Math.max(...consultas.map(c => c.id), 0) + 1;
    
    // Determinando o intervalo baseado no tipo de recorrência
    let intervalo = formData.intervaloRecorrencia;
    let incrementoDias = 7; // Default para semanal
    
    switch (formData.tipoRecorrencia) {
      case 'semanal':
        incrementoDias = 7 * intervalo;
        break;
      case 'quinzenal':
        incrementoDias = 14 * intervalo;
        break;
      case 'mensal':
        // Para recorrência mensal, vamos incrementar meses em vez de dias
        // Vamos criar uma data por vez e avançar o mês
        while (dataAtual <= dataLimite) {
          const dataFormatada = dataAtual.toLocaleDateString('pt-BR');
          const horarioFormatado = `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
          
          // Cria a consulta
          consultasRecorrentes.push({
            id: idBase++,
            pacienteId: pacienteId,
            data: dataFormatada,
            horario: horarioFormatado,
            duracao: formData.duracaoRecorrente,
            tipo: formData.tipo,
            plataforma: formData.plataforma,
            status: 'Agendada',
            observacoes: formData.observacoes,
            psicologoId: psicologoId,
            acompanhamentos: [],
            isRecorrente: true,
            tipoRecorrencia: formData.tipoRecorrencia
          });
          
          // Avança para o próximo mês (considerando o intervalo)
          const mesAtual = dataAtual.getMonth();
          dataAtual.setMonth(mesAtual + intervalo);
          
          // Se mudar para um dia inválido (por exemplo, 31 de janeiro -> 31 de fevereiro não existe)
          // ajustar para o último dia do mês
          const novoDia = dataAtual.getDate();
          const diaOriginal = dataInicio.getDate();
          if (novoDia !== diaOriginal) {
            dataAtual.setDate(0); // Último dia do mês anterior
          }
        }
        
        // Adiciona as consultas recorrentes
        setConsultas([...consultas, ...consultasRecorrentes]);
        
        let tipoRecorrenciaTexto = 'mensais';
        if (intervalo > 1) {
          tipoRecorrenciaTexto = `a cada ${intervalo} meses`;
        }
        
        alert(`Foram criadas ${consultasRecorrentes.length} consultas recorrentes para ${paciente.nome} ${tipoRecorrenciaTexto} às ${formData.horarioRecorrente}.`);
        
        handleCloseDialog();
        return;
    }
    
    // Para recorrência semanal e quinzenal
    while (dataAtual <= dataLimite) {
      const dataFormatada = dataAtual.toLocaleDateString('pt-BR');
      const horarioFormatado = `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
      
      // Cria a consulta
      consultasRecorrentes.push({
        id: idBase++,
        pacienteId: pacienteId,
        data: dataFormatada,
        horario: horarioFormatado,
        duracao: formData.duracaoRecorrente,
        tipo: formData.tipo,
        plataforma: formData.plataforma,
        status: 'Agendada',
        observacoes: formData.observacoes,
        psicologoId: psicologoId,
        acompanhamentos: [],
        isRecorrente: true,
        tipoRecorrencia: formData.tipoRecorrencia
      });
      
      // Avança para o próximo período conforme o incremento calculado
      dataAtual.setDate(dataAtual.getDate() + incrementoDias);
    }
    
    // Adiciona as consultas recorrentes
    setConsultas([...consultas, ...consultasRecorrentes]);
    
    let tipoRecorrenciaTexto = '';
    switch (formData.tipoRecorrencia) {
      case 'semanal':
        tipoRecorrenciaTexto = intervalo > 1 
          ? `a cada ${intervalo} semanas às ${formData.horarioRecorrente}`
          : `todas as ${translateDayName(formData.diaSemanaRecorrente)}s às ${formData.horarioRecorrente}`;
        break;
      case 'quinzenal':
        tipoRecorrenciaTexto = intervalo > 1 
          ? `a cada ${intervalo * 2} semanas às ${formData.horarioRecorrente}`
          : `quinzenalmente às ${formData.horarioRecorrente}`;
        break;
    }
    
    alert(`Foram criadas ${consultasRecorrentes.length} consultas recorrentes para ${paciente.nome} ${tipoRecorrenciaTexto}.`);
    
    handleCloseDialog();
  };

  // Função auxiliar para traduzir o nome do dia da semana
  const translateDayName = (day) => {
    const translations = {
      'domingo': 'Domingo',
      'segunda': 'Segunda-feira',
      'terca': 'Terça-feira',
      'quarta': 'Quarta-feira',
      'quinta': 'Quinta-feira',
      'sexta': 'Sexta-feira',
      'sabado': 'Sábado'
    };
    return translations[day] || day;
  };

  const handleSaveAcompanhamento = () => {
    if (!acompanhamento.trim()) return;
    
    const consultasAtualizadas = consultas.map(c => {
      if (c.id === currentConsulta.id) {
        const novoAcompanhamento = {
          id: Math.max(...(c.acompanhamentos?.map(a => a.id) || [0]), 0) + 1,
          data: new Date().toLocaleDateString('pt-BR'),
          conteudo: acompanhamento
        };
        
        return {
          ...c,
          acompanhamentos: [...(c.acompanhamentos || []), novoAcompanhamento]
        };
      }
      return c;
    });
    
    setConsultas(consultasAtualizadas);
    handleCloseAcompanhamentoDialog();
  };

  const handleDeleteConsulta = (consultaId) => {
    if (window.confirm('Tem certeza que deseja excluir esta consulta?')) {
      setConsultas(consultas.filter(c => c.id !== consultaId));
    }
  };

  const handleChangeStatus = (consultaId, novoStatus) => {
    const consultasAtualizadas = consultas.map(c => {
      if (c.id === consultaId) {
        return { ...c, status: novoStatus };
      }
      return c;
    });
    setConsultas(consultasAtualizadas);
  };

  const formatarData = (data) => {
    if (!data) return '';
    try {
      const [dia, mes, ano] = data.split('/');
      return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return data;
    }
  };

  const getPacienteNome = (pacienteId) => {
    const paciente = pacientes.find(p => p.id === pacienteId);
    return paciente ? paciente.nome : 'Paciente não encontrado';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Agendada':
        return { bg: '#2196f3', color: 'white' };
      case 'Realizada':
        return { bg: '#4caf50', color: 'white' };
      case 'Cancelada':
        return { bg: '#f44336', color: 'white' };
      case 'Remarcada':
        return { bg: '#ff9800', color: 'white' };
      default:
        return { bg: '#757575', color: 'white' };
    }
  };

  const filteredConsultas = () => {
    if (tabValue === 0) {
      // Todas
      return consultas;
    } else if (tabValue === 1) {
      // Hoje
      const hoje = new Date().toLocaleDateString('pt-BR');
      return consultas.filter(c => c.data === hoje);
    } else if (tabValue === 2) {
      // Agendadas
      return consultas.filter(c => c.status === 'Agendada');
    } else if (tabValue === 3) {
      // Realizadas
      return consultas.filter(c => c.status === 'Realizada');
    } else if (tabValue === 4) {
      // Data específica
      if (!selectedDate) return [];
      const dataFormatada = selectedDate.toLocaleDateString('pt-BR');
      return consultas.filter(c => c.data === dataFormatada);
    }
    return consultas;
  };

  const handleAceitarSolicitacao = (solicitacaoId) => {
    // Obter solicitação
    const solicitacao = solicitacoesPendentes.find(s => s.id === solicitacaoId);
    
    if (solicitacao.tipo === 'reagendamento') {
      // Atualizar a consulta existente com a nova data e horário
      const consultasAtualizadas = consultas.map(c => {
        if (c.id === solicitacao.consultaId) {
          return {
            ...c,
            data: solicitacao.dataSolicitada,
            horario: solicitacao.horarioSolicitado,
            status: 'Agendada'
          };
        }
        return c;
      });
      setConsultas(consultasAtualizadas);
    } else if (solicitacao.tipo === 'novaConsulta') {
      // Criar uma nova consulta com os dados da solicitação
      const novaConsulta = {
        id: Math.max(...consultas.map(c => c.id), 0) + 1,
        pacienteId: solicitacao.pacienteId,
        data: solicitacao.dataSolicitada,
        horario: solicitacao.horarioSolicitado,
        duracao: '50',
        tipo: solicitacao.tipoConsulta,
        plataforma: solicitacao.tipoConsulta === 'Online' ? 'Zoom' : '',
        status: 'Agendada',
        observacoes: solicitacao.observacoes || '',
        psicologoId: psicologoId,
        acompanhamentos: []
      };
      
      setConsultas([...consultas, novaConsulta]);
    }
    
    // Remover a solicitação da lista de pendentes
    setSolicitacoesPendentes(solicitacoesPendentes.filter(s => s.id !== solicitacaoId));
    
    // Notificar o usuário
    alert('Solicitação aceita com sucesso!');
  };
  
  const handleRejeitarSolicitacao = (solicitacaoId) => {
    // Em um sistema real, aqui enviaria uma notificação para o paciente
    // Remover a solicitação da lista de pendentes
    setSolicitacoesPendentes(solicitacoesPendentes.filter(s => s.id !== solicitacaoId));
    
    // Notificar o usuário
    alert('Solicitação rejeitada!');
  };
  
  // Adicionar um componente para exibir as solicitações pendentes
  const renderSolicitacoesPendentes = () => {
    if (solicitacoesPendentes.length === 0) {
      return null;
    }
    
    return (
      <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #ff9800' }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h6" sx={{ color: '#ff9800', display: 'flex', alignItems: 'center' }}>
            <HourglassEmptyIcon sx={{ mr: 1 }} />
            Solicitações Pendentes
          </Typography>
          <Chip 
            label={`${solicitacoesPendentes.length} pendente${solicitacoesPendentes.length > 1 ? 's' : ''}`} 
            color="warning" 
            size="small" 
          />
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Você tem {solicitacoesPendentes.length} solicitação(ões) pendente(s) que requerem sua aprovação.
          </Typography>
        </Box>
        
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Detalhes</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {solicitacoesPendentes.map(solicitacao => {
                const paciente = pacientes.find(p => p.id === solicitacao.pacienteId);
                
                return (
                  <TableRow key={solicitacao.id}>
                    <TableCell>
                      {solicitacao.tipo === 'reagendamento' ? (
                        <Chip 
                          label="Reagendamento" 
                          size="small"
                          sx={{ bgcolor: '#ff9800', color: 'white' }}
                        />
                      ) : (
                        <Chip 
                          label="Nova Consulta" 
                          size="small"
                          sx={{ bgcolor: '#2196f3', color: 'white' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{paciente ? paciente.nome : 'Paciente não encontrado'}</TableCell>
                    <TableCell>
                      {solicitacao.tipo === 'reagendamento' ? (
                        <>
                          <Typography variant="body2">
                            De: <strong>{solicitacao.dataAtual}</strong> às <strong>{solicitacao.horarioAtual}</strong>
                          </Typography>
                          <Typography variant="body2">
                            Para: <strong>{solicitacao.dataSolicitada}</strong> às <strong>{solicitacao.horarioSolicitado}</strong>
                          </Typography>
                          {solicitacao.motivo && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Motivo: {solicitacao.motivo}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">
                            Data: <strong>{solicitacao.dataSolicitada}</strong> às <strong>{solicitacao.horarioSolicitado}</strong>
                          </Typography>
                          <Typography variant="body2">
                            Tipo: <strong>{solicitacao.tipoConsulta}</strong>
                          </Typography>
                          {solicitacao.observacoes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Obs: {solicitacao.observacoes}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleAceitarSolicitacao(solicitacao.id)}
                          sx={{ minWidth: 'unset', px: 1 }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => handleRejeitarSolicitacao(solicitacao.id)}
                          sx={{ minWidth: 'unset', px: 1 }}
                        >
                          <CancelIcon fontSize="small" />
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Consultas
      </Typography>
      
      {/* Exibir solicitações pendentes */}
      {renderSolicitacoesPendentes()}
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
        >
          Nova Consulta
        </Button>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { minWidth: 100 },
              '& .Mui-selected': { color: '#4a8a84' },
              '& .MuiTabs-indicator': { backgroundColor: '#4a8a84' }
            }}
          >
            <Tab label="Todas" />
            <Tab label="Hoje" />
            <Tab label="Agendadas" />
            <Tab label="Realizadas" />
            <Tab label="Por Data" />
          </Tabs>
          
          {tabValue === 4 && (
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              sx={{ ml: 2, minWidth: 200 }}
            />
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabela de consultas">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Paciente</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Horário</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConsultas().length > 0 ? (
              filteredConsultas()
                .sort((a, b) => {
                  // Ordenação por data e horário
                  const dataA = a.data.split('/').reverse().join('-');
                  const dataB = b.data.split('/').reverse().join('-');
                  const dateTimeA = new Date(`${dataA}T${a.horario}`);
                  const dateTimeB = new Date(`${dataB}T${b.horario}`);
                  return dateTimeA - dateTimeB;
                })
                .map((consulta) => (
                  <TableRow
                    key={consulta.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {getPacienteNome(consulta.pacienteId)}
                      {consulta.isRecorrente && (
                        <Chip 
                          label="Recorrente" 
                          size="small"
                          sx={{ ml: 1, bgcolor: '#e0f2f1', color: '#00897b' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatarData(consulta.data)}</TableCell>
                    <TableCell>{consulta.horario}</TableCell>
                    <TableCell>
                      {consulta.tipo}
                      {consulta.tipo === 'Online' && consulta.plataforma && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          via {consulta.plataforma}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={consulta.status} 
                        size="small"
                        sx={{ 
                          bgcolor: getStatusColor(consulta.status).bg,
                          color: getStatusColor(consulta.status).color
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(consulta)}
                            sx={{ color: '#4a8a84' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {consulta.status === 'Agendada' && (
                          <>
                            <Tooltip title="Marcar como realizada">
                              <IconButton 
                                size="small" 
                                onClick={() => handleChangeStatus(consulta.id, 'Realizada')}
                                sx={{ color: '#4caf50' }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Cancelar consulta">
                              <IconButton 
                                size="small" 
                                onClick={() => handleChangeStatus(consulta.id, 'Cancelada')}
                                sx={{ color: '#f44336' }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        
                        {consulta.status === 'Realizada' && (
                          <Tooltip title="Adicionar acompanhamento">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenAcompanhamentoDialog(consulta)}
                              sx={{ color: '#ff9800' }}
                            >
                              <NoteAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteConsulta(consulta.id)}
                            sx={{ color: '#757575' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhuma consulta encontrada para os filtros selecionados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Dialog para criar/editar consulta */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentConsulta ? 'Editar Consulta' : 'Nova Consulta'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Opção para consulta recorrente */}
            {!currentConsulta && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRecorrente}
                      onChange={(e) => handleInputChange('isRecorrente', e.target.checked)}
                    />
                  }
                  label="Consulta recorrente"
                  sx={{ mb: 2 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.pacienteId}>
                <InputLabel>Paciente</InputLabel>
                <Select
                  value={formData.pacienteId}
                  label="Paciente"
                  onChange={(e) => handleInputChange('pacienteId', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Selecione um paciente</em>
                  </MenuItem>
                  {pacientes.map((paciente) => (
                    <MenuItem key={paciente.id} value={paciente.id.toString()}>
                      {paciente.nome}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.pacienteId && (
                  <FormHelperText>{formErrors.pacienteId}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  disabled={formData.isRecorrente}
                >
                  <MenuItem value="Agendada">Agendada</MenuItem>
                  <MenuItem value="Realizada">Realizada</MenuItem>
                  <MenuItem value="Cancelada">Cancelada</MenuItem>
                  <MenuItem value="Remarcada">Remarcada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {formData.isRecorrente ? (
              // Campos para consulta recorrente
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Recorrência</InputLabel>
                    <Select
                      value={formData.tipoRecorrencia}
                      label="Tipo de Recorrência"
                      onChange={(e) => handleInputChange('tipoRecorrencia', e.target.value)}
                    >
                      <MenuItem value="semanal">Semanal</MenuItem>
                      <MenuItem value="quinzenal">Quinzenal</MenuItem>
                      <MenuItem value="mensal">Mensal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Intervalo"
                    type="number"
                    value={formData.intervaloRecorrencia}
                    onChange={(e) => handleInputChange('intervaloRecorrencia', e.target.value)}
                    helperText={
                      formData.tipoRecorrencia === 'semanal' ? 'A cada X semanas' :
                      formData.tipoRecorrencia === 'quinzenal' ? 'A cada X quinzenas' :
                      'A cada X meses'
                    }
                    inputProps={{ min: 1, max: 12 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.diaSemanaRecorrente}>
                    <InputLabel>Dia da semana</InputLabel>
                    <Select
                      value={formData.diaSemanaRecorrente}
                      label="Dia da semana"
                      onChange={(e) => handleInputChange('diaSemanaRecorrente', e.target.value)}
                    >
                      <MenuItem value="segunda">Segunda-feira</MenuItem>
                      <MenuItem value="terca">Terça-feira</MenuItem>
                      <MenuItem value="quarta">Quarta-feira</MenuItem>
                      <MenuItem value="quinta">Quinta-feira</MenuItem>
                      <MenuItem value="sexta">Sexta-feira</MenuItem>
                      <MenuItem value="sabado">Sábado</MenuItem>
                      <MenuItem value="domingo">Domingo</MenuItem>
                    </Select>
                    {formErrors.diaSemanaRecorrente && (
                      <FormHelperText>{formErrors.diaSemanaRecorrente}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Horário"
                    type="time"
                    value={formData.horarioRecorrente}
                    onChange={(e) => handleInputChange('horarioRecorrente', e.target.value)}
                    error={!!formErrors.horarioRecorrente}
                    helperText={formErrors.horarioRecorrente}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duração (minutos)"
                    type="number"
                    value={formData.duracaoRecorrente}
                    onChange={(e) => handleInputChange('duracaoRecorrente', e.target.value)}
                    inputProps={{ min: 10, max: 180 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data limite para recorrência (opcional)"
                    type="date"
                    value={formData.dataFimRecorrencia}
                    onChange={(e) => handleInputChange('dataFimRecorrencia', e.target.value)}
                    helperText="Se não informado, serão criadas consultas para os próximos 6 meses"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formData.tipoRecorrencia === 'semanal' && 'Isto criará consultas recorrentes semanais no dia da semana e horário especificados.'}
                    {formData.tipoRecorrencia === 'quinzenal' && 'Isto criará consultas recorrentes a cada duas semanas no dia da semana e horário especificados.'}
                    {formData.tipoRecorrencia === 'mensal' && 'Isto criará consultas recorrentes mensais na mesma data do mês e horário especificados.'}
                  </Typography>
                </Grid>
              </>
            ) : (
              // Campos para consulta normal
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Data da Consulta"
                    type="date"
                    value={formData.data}
                    onChange={(e) => handleInputChange('data', e.target.value)}
                    error={!!formErrors.data}
                    helperText={formErrors.data}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Horário da Consulta"
                    type="time"
                    value={formData.horario}
                    onChange={(e) => handleInputChange('horario', e.target.value)}
                    error={!!formErrors.horario}
                    helperText={formErrors.horario}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Duração (minutos)"
                    type="number"
                    value={formData.duracao}
                    onChange={(e) => handleInputChange('duracao', e.target.value)}
                    error={!!formErrors.duracao}
                    helperText={formErrors.duracao}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Consulta</InputLabel>
                <Select
                  value={formData.tipo}
                  label="Tipo de Consulta"
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                >
                  <MenuItem value="Presencial">Presencial</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="Domiciliar">Domiciliar</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plataforma (se online)"
                value={formData.plataforma}
                onChange={(e) => handleInputChange('plataforma', e.target.value)}
                disabled={formData.tipo !== 'Online'}
                error={!!formErrors.plataforma}
                helperText={formErrors.plataforma}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={4}
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveConsulta} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            {formData.isRecorrente ? 'Criar Consultas Recorrentes' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para adicionar acompanhamento */}
      <Dialog 
        open={openAcompanhamentoDialog} 
        onClose={handleCloseAcompanhamentoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Adicionar Acompanhamento</DialogTitle>
        <DialogContent>
          {currentConsulta && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Consulta com {getPacienteNome(currentConsulta.pacienteId)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatarData(currentConsulta.data)} às {currentConsulta.horario}
              </Typography>
            </Box>
          )}
          
          <TextField
            autoFocus
            label="Anotações do acompanhamento"
            multiline
            rows={6}
            fullWidth
            value={acompanhamento}
            onChange={(e) => setAcompanhamento(e.target.value)}
          />
          
          {currentConsulta && currentConsulta.acompanhamentos && currentConsulta.acompanhamentos.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Acompanhamentos anteriores:
              </Typography>
              {currentConsulta.acompanhamentos.map((a) => (
                <Paper key={a.id} sx={{ p: 2, mb: 1, bgcolor: '#f5f5f5' }}>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {a.data}
                  </Typography>
                  <Typography variant="body2">
                    {a.conteudo}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAcompanhamentoDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveAcompanhamento} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Consultas; 